import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../hooks/useLanguage';
import storiesData from '../../data/storiesData.json';
import TextToSpeech from '../TextToSpeech';
import './Stories.css';

// Importar todas las imágenes
import image1 from '../../assets/images/image1.jpg';
import image2 from '../../assets/images/image2.jpg';
import image3 from '../../assets/images/image3.jpg';
import image4 from '../../assets/images/image4.jpg';
import image5 from '../../assets/images/image5.jpg';
import image6 from '../../assets/images/image6.jpg';
import image7 from '../../assets/images/image7.jpg';
import image8 from '../../assets/images/image8.jpg';
import video1 from '../../assets/images/video1.mp4';

// Mapear las rutas a las imágenes importadas
const imageMap = {
  '/src/assets/images/image1.jpg': image1,
  '/src/assets/images/image2.jpg': image2,
  '/src/assets/images/image3.jpg': image3,
  '/src/assets/images/image4.jpg': image4,
  '/src/assets/images/image5.jpg': image5,
  '/src/assets/images/image6.jpg': image6,
  '/src/assets/images/image7.jpg': image7,
  '/src/assets/images/image8.jpg': image8,
  '/src/assets/images/video1.mp4': video1
};

const Stories = () => {
  const { getText } = useLanguage();
  const [selectedStory, setSelectedStory] = useState(null);
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [readingMode, setReadingMode] = useState('full'); // 'full' or 'chapter'
  const [expandedChapters, setExpandedChapters] = useState({});
  const [readingProgress, setReadingProgress] = useState({});
  const [autoPlayChapter, setAutoPlayChapter] = useState(false);

  // Cargar progreso de lectura desde localStorage
  useEffect(() => {
    const savedProgress = localStorage.getItem('stories-reading-progress');
    if (savedProgress) {
      try {
        setReadingProgress(JSON.parse(savedProgress));
      } catch (error) {
        console.error('Error loading reading progress:', error);
      }
    }
  }, []);

  // Guardar progreso de lectura en localStorage
  const saveReadingProgress = (storyId, chapterId) => {
    const newProgress = {
      ...readingProgress,
      [storyId]: {
        lastChapterId: chapterId,
        timestamp: new Date().toISOString()
      }
    };
    setReadingProgress(newProgress);
    localStorage.setItem('stories-reading-progress', JSON.stringify(newProgress));
  };

  // Cargar las historias desde el archivo JSON
  useEffect(() => {
    try {
      setStories(storiesData.stories);
      setLoading(false);
    } catch (error) {
      console.error('Error loading stories:', error);
      setLoading(false);
    }
  }, []);

  // Efecto para manejar autoplay de capítulos
  useEffect(() => {
    if (autoPlayChapter && selectedChapter && readingMode === 'chapter') {
      console.log('AutoPlay de capítulo activado para:', selectedChapter.title);
      
      // Reset autoplay flag después de un pequeño delay
      setTimeout(() => {
        setAutoPlayChapter(false);
      }, 500);
    }
  }, [selectedChapter, autoPlayChapter, readingMode]);

  // Función para obtener la imagen real desde el mapa
  const getImage = (imagePath) => {
    return imageMap[imagePath] || null;
  };

  // Función para extraer todo el texto de la historia para leer en voz alta
  const getStoryText = (story) => {
    if (!story) {
      console.log('No hay historia para leer');
      return '';
    }
    
    let fullText = '';
    
    // Agregar título y subtítulo
    if (story.title) {
      fullText += `${story.title}. `;
    }
    if (story.subtitle) {
      fullText += `${story.subtitle}. `;
    }
    
    // Agregar descripción
    if (story.description) {
      fullText += `${story.description}. `;
    }
    
    // Agregar capítulos
    if (story.chapters && story.chapters.length > 0) {
      story.chapters.forEach((chapter) => {
        if (chapter.title) {
          // Solo agregar el título del capítulo tal como está, sin duplicar "Capítulo"
          fullText += `${chapter.title}. `;
        }
        if (chapter.content) {
          // Limpiar saltos de línea múltiples y espacios extra
          const cleanContent = chapter.content
            .replace(/\n\n+/g, '. ')
            .replace(/\n/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
          fullText += cleanContent + '. ';
        }
      });
    }
    
    const finalText = fullText.trim();
    console.log(`Texto generado para TTS: ${finalText.length} caracteres`);
    console.log('Primeros 200 caracteres:', finalText.substring(0, 200));
    
    return finalText;
  };

  // Función para extraer el texto de un capítulo específico
  const getChapterText = (chapter) => {
    if (!chapter) {
      console.log('No hay capítulo para leer');
      return '';
    }
    
    let chapterText = '';
    
    // Agregar título del capítulo
    if (chapter.title) {
      chapterText += `${chapter.title}. `;
    }
    
    // Agregar contenido del capítulo
    if (chapter.content) {
      const cleanContent = chapter.content
        .replace(/\n\n+/g, '. ')
        .replace(/\n/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      chapterText += cleanContent + '. ';
    }
    
    const finalText = chapterText.trim();
    console.log(`Texto del capítulo para TTS: ${finalText.length} caracteres`);
    console.log('Primeros 200 caracteres:', finalText.substring(0, 200));
    
    return finalText;
  };

  // Función para obtener el texto según el modo de lectura seleccionado
  const getTextToRead = () => {
    if (readingMode === 'chapter' && selectedChapter) {
      return getChapterText(selectedChapter);
    } else {
      return getStoryText(selectedStory);
    }
  };

  const handleStorySelect = (story) => {
    setSelectedStory(story);
    setSelectedChapter(null);
    setReadingMode('full');
    
    // Cargar progreso de lectura y expandir el último capítulo leído
    const progress = readingProgress[story.id];
    if (progress && story.chapters) {
      const lastChapter = story.chapters.find(ch => ch.id == progress.lastChapterId);
      if (lastChapter) {
        setExpandedChapters({ [lastChapter.id]: true });
        // Hacer scroll al último capítulo después de que se renderice
        setTimeout(() => {
          scrollToChapter(lastChapter.id);
        }, 300);
      } else {
        // Si no hay progreso, expandir el primer capítulo
        setExpandedChapters({ [story.chapters[0].id]: true });
      }
    } else if (story.chapters && story.chapters.length > 0) {
      setExpandedChapters({ [story.chapters[0].id]: true });
    }
  };

  const scrollToChapter = (chapterId) => {
    const chapterElement = document.querySelector(`[data-chapter-id="${chapterId}"]`);
    if (chapterElement) {
      chapterElement.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
    }
  };

  const handleChapterTTSClick = (chapter) => {
    // Establecer el capítulo como seleccionado
    setSelectedChapter(chapter);
    setReadingMode('chapter');
    setAutoPlayChapter(true);
    
    // Guardar progreso
    if (selectedStory) {
      saveReadingProgress(selectedStory.id, chapter.id);
    }
    
    console.log('Capítulo seleccionado para reproducir:', chapter.title);
  };

  const handleBackToList = () => {
    setSelectedStory(null);
    setSelectedChapter(null);
    setReadingMode('full');
    setExpandedChapters({});
  };

  const handleChapterSelect = (chapter) => {
    setSelectedChapter(chapter);
    // Guardar progreso de lectura
    if (selectedStory) {
      saveReadingProgress(selectedStory.id, chapter.id);
    }
  };

  const handleReadingModeChange = (mode) => {
    setReadingMode(mode);
    if (mode === 'chapter' && !selectedChapter && selectedStory?.chapters?.length > 0) {
      setSelectedChapter(selectedStory.chapters[0]);
    }
  };

  const toggleChapter = (chapterId) => {
    setExpandedChapters(prev => ({
      ...prev,
      [chapterId]: !prev[chapterId]
    }));
    
    // Guardar progreso cuando se abre un capítulo
    if (!expandedChapters[chapterId] && selectedStory) {
      saveReadingProgress(selectedStory.id, chapterId);
    }
  };

  const getChapterProgress = (storyId, chapterId) => {
    const progress = readingProgress[storyId];
    return progress && progress.lastChapterId == chapterId;
  };

  if (loading) {
    return (
      <div className="stories-container">
        <div className="loading-container">
          <h2>📚 {getText('stories.loading')}</h2>
        </div>
      </div>
    );
  }

  if (selectedStory) {
    return (
      <div className="stories-container">
        <div className="story-reader">
          <button onClick={handleBackToList} className="back-button">
            ← {getText('stories.backToStories')}
          </button>
          
          <div className="story-header">
            <h1>{selectedStory.title}</h1>
            <p className="story-subtitle">{selectedStory.subtitle}</p>
            {selectedStory.coverImage && getImage(selectedStory.coverImage) && (
              <div className="story-cover">
                <img src={getImage(selectedStory.coverImage)} alt={selectedStory.title} />
              </div>
            )}
          </div>

          <div className="story-description">
            <p>{selectedStory.description}</p>
          </div>

          <div className="story-meta">
            <div className="story-info">
              <span className="story-author">📝 {getText('stories.author')}: {selectedStory.author}</span>
              <span className="story-status">📊 {getText('stories.status')}: {selectedStory.status}</span>
              <span className="story-chapters">📚 {getText('stories.chapters')}: {selectedStory.chapters?.length || 0}</span>
            </div>
            {selectedStory.tags && (
              <div className="story-tags">
                {selectedStory.tags.map((tag, index) => (
                  <span key={index} className="story-tag">#{tag}</span>
                ))}
              </div>
            )}
          </div>

          {/* Controles de modo de lectura */}
          <div className="tts-controls-panel">
            <div className="reading-mode-selector">
              <h4>🔊 Modo de Lectura:</h4>
              <div className="mode-buttons">
                <button 
                  className={`mode-button ${readingMode === 'full' ? 'active' : ''}`}
                  onClick={() => handleReadingModeChange('full')}
                >
                  📚 Historia Completa
                </button>
                <button 
                  className={`mode-button ${readingMode === 'chapter' ? 'active' : ''}`}
                  onClick={() => handleReadingModeChange('chapter')}
                >
                  📖 Solo Capítulo
                </button>
              </div>
            </div>

            {readingMode === 'chapter' && selectedStory?.chapters && (
              <div className="chapter-selector">
                <h4>📋 Seleccionar Capítulo:</h4>
                <select 
                  value={selectedChapter?.id || ''} 
                  onChange={(e) => {
                    const chapter = selectedStory.chapters.find(ch => ch.id == e.target.value);
                    handleChapterSelect(chapter);
                  }}
                  className="chapter-select"
                >
                  {selectedStory.chapters.map((chapter, index) => (
                    <option key={chapter.id || index} value={chapter.id || index}>
                      {chapter.title}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="chapters-container">
            {selectedStory.chapters?.map((chapter, index) => (
              <div 
                key={chapter.id || index} 
                className="chapter"
                data-chapter-id={chapter.id || index}
              >
                <div 
                  className="chapter-header-collapsible" 
                  onClick={() => toggleChapter(chapter.id || index)}
                >
                  <div className="chapter-title-row">
                    <h2>{chapter.title}</h2>
                    <div className="chapter-controls">
                      {getChapterProgress(selectedStory.id, chapter.id || index) && (
                        <span className="chapter-progress-indicator" title="Último capítulo leído">📖</span>
                      )}
                      <button 
                        className="chapter-tts-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleChapterTTSClick(chapter);
                        }}
                        title="Reproducir este capítulo"
                      >
                        ▶
                      </button>
                      
                      {/* TextToSpeech individual para cada capítulo */}
                      {selectedChapter?.id === chapter.id && readingMode === 'chapter' && (
                        <TextToSpeech 
                          key={`chapter-${chapter.id}`}
                          text={getChapterText(chapter)}
                          position="bottom-left"
                          size="large"
                          color="primary"
                          rate={0.9}
                          pitch={1.1}
                          autoPlay={autoPlayChapter}
                          language="es"
                        />
                      )}
                      <span className="chapter-toggle-icon">
                        {expandedChapters[chapter.id || index] ? '▼' : '▶'}
                      </span>
                    </div>
                  </div>
                </div>

                {expandedChapters[chapter.id || index] && (
                  <div className="chapter-content-wrapper">
                    {chapter.image && getImage(chapter.image) && (
                      <div className="chapter-image">
                        <img src={getImage(chapter.image)} alt={chapter.title} />
                      </div>
                    )}
                    <div className="chapter-content">
                      {chapter.content.split('\n\n').map((paragraph, pIndex) => (
                        <p key={pIndex}>{paragraph}</p>
                      ))}
                    </div>
                    {chapter.video && getImage(chapter.video) && (
                      <div className="chapter-video">
                        <video controls>
                          <source src={getImage(chapter.video)} type="video/mp4" />
                          Tu navegador no soporta el elemento video.
                        </video>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="story-footer">
            <p className="story-end">📚 {getText('stories.chapterEnd')}</p>
            <p className="story-note">💝 {getText('stories.createdWithLove')} {selectedStory.author}</p>
            <p className="story-update">🔄 {getText('stories.lastUpdated')}: {storiesData.metadata.lastUpdated}</p>
          </div>

          {/* Botón flotante para leer la historia completa */}
          {readingMode === 'full' && (
            <TextToSpeech 
              text={getStoryText(selectedStory)}
              position="bottom-left"
              size="large"
              color="primary"
              rate={0.9}
              pitch={1.1}
              language="es"
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="stories-container">
      <div className="stories-header">
        <h1>📚 {getText('stories.title')}</h1>
        <p>{getText('stories.subtitle')}</p>
      </div>

      <div className="stories-grid">
        {stories.map((story) => (
          <div key={story.id} className="story-card" onClick={() => handleStorySelect(story)}>
            <div className="story-card-image">
              {story.coverImage && getImage(story.coverImage) ? (
                <img src={getImage(story.coverImage)} alt={story.title} />
              ) : (
                <span>🌌</span>
              )}
              {readingProgress[story.id] && (
                <div className="story-progress-badge" title="Historia en progreso">
                  📖
                </div>
              )}
            </div>
            <div className="story-card-content">
              <h3>{story.title}</h3>
              <p className="story-card-subtitle">{story.subtitle}</p>
              <p className="story-card-description">{story.description.substring(0, 150)}...</p>
              <div className="story-card-meta">
                <span className="story-card-author">📝 {story.author}</span>
                <span className="story-card-chapters">📚 {story.chapters?.length || 0} {getText('stories.chapters').toLowerCase()}.</span>
              </div>
              {readingProgress[story.id] && (
                <div className="story-progress-info">
                  <span>📍 Último capítulo leído</span>
                </div>
              )}
              <button className="read-story-btn">{getText('stories.readStory')}</button>
            </div>
          </div>
        ))}
      </div>

      <div className="stories-footer">
        <div className="stories-stats">
          <p>📊 {getText('stories.totalStories')}: {storiesData.metadata.totalStories}</p>
          <p>📚 {getText('stories.totalChapters')}: {storiesData.metadata.totalChapters}</p>
          <p>🔄 {getText('stories.lastUpdated')}: {storiesData.metadata.lastUpdated}</p>
        </div>
        <p>✨ {getText('stories.moreStories')}</p>
      </div>
    </div>
  );
};

export default Stories;
